import { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  LayoutDashboard, Building2, UserCircle, Target,
  KanbanSquare, CheckSquare, LogOut, Menu, Users, Calendar, User, Eye, Key, FileText
} from 'lucide-react';
import NotificationDropdown from './NotificationDropdown';
import AiAssistantPanel from './AiAssistantPanel';

const navItems = [
  { to: '/', label: 'Дашборд', icon: LayoutDashboard },
  { to: '/calendar', label: 'Календарь', icon: Calendar },
  { to: '/deals', label: 'Сделки', icon: KanbanSquare },
  { to: '/tasks', label: 'Задачи', icon: CheckSquare },
  { to: '/leads', label: 'Лиды', icon: Target },
  { to: '/companies', label: 'Компании', icon: Building2 },
  { to: '/contacts', label: 'Контакты', icon: UserCircle },
];

interface Props {
  children: JSX.Element;
}

export default function Layout({ children }: Props) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isFullPage = location.pathname === '/calendar';
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="app-layout">
      <div
        className={`modal-overlay ${menuOpen ? 'show' : ''}`}
        style={{ display: menuOpen ? 'block' : 'none', zIndex: 90 }}
        onClick={() => setMenuOpen(false)}
      />
      <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">SakhaVision CRM</div>
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => isActive ? 'active' : ''}
              onClick={() => setMenuOpen(false)}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
          {user?.role === 'ADMIN' && (
            <>
              <NavLink
                to="/admin"
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={() => setMenuOpen(false)}
              >
                <Users size={20} />
                <span>Пользователи</span>
              </NavLink>
              <NavLink
                to="/api-keys"
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={() => setMenuOpen(false)}
              >
                <Key size={20} />
                <span>API-ключи</span>
              </NavLink>
              <NavLink
                to="/api-docs"
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={() => setMenuOpen(false)}
              >
                <FileText size={20} />
                <span>Документация API</span>
              </NavLink>
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user?.firstName} {user?.lastName}</div>
            <div className="sidebar-user-role">{user?.email}</div>
          </div>
          <button onClick={handleLogout} className="btn btn-danger btn-sm" style={{ width: '100%', justifyContent: 'flex-start' }}>
            <LogOut size={16} /> Выйти
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="header">
          <div className="flex items-center gap-4">
            <button className="mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
              <Menu size={24} />
            </button>
            <div className="header-title" style={{ display: 'none' }}>SakhaVision</div>
          </div>

          <div className="header-user-actions">
            <button
              className="header-icon-btn ai-header-btn"
              onClick={() => setAiPanelOpen(true)}
              title="ИИ-Ассистент"
            >
              <Eye size={26} />
            </button>
            <NotificationDropdown />
            <div className="flex items-center gap-2" ref={userMenuRef} style={{ position: 'relative' }}>
              <button
                className="header-avatar"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                style={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0 }}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </button>
              <div style={{ fontSize: 14, fontWeight: 500 }} className="desktop-only">
                {user?.firstName}
              </div>
              {userMenuOpen && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 8,
                  background: 'var(--color-bg-primary, #fff)',
                  border: '1px solid var(--color-border-soft, #e2e8f0)',
                  borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  minWidth: 180, zIndex: 100, overflow: 'hidden',
                }}>
                  <button
                    onClick={() => { navigate('/profile'); setUserMenuOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '10px 16px', border: 'none',
                      background: 'none', cursor: 'pointer', fontSize: 14,
                      textAlign: 'left', color: 'var(--color-text-primary, #1a202c)',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg-hover, #f7fafc)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    <User size={16} /> Профиль
                  </button>
                  <button
                    onClick={handleLogout}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '10px 16px', border: 'none',
                      background: 'none', cursor: 'pointer', fontSize: 14,
                      textAlign: 'left', color: 'var(--color-danger, #c00)',
                      borderTop: '1px solid var(--color-border-soft, #e2e8f0)',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg-hover, #f7fafc)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    <LogOut size={16} /> Выйти
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <div className={`page-content ${isFullPage ? 'page-content--full' : ''}`}>
          {children}
        </div>
      </main>

      <AiAssistantPanel isOpen={aiPanelOpen} onClose={() => setAiPanelOpen(false)} />
    </div>
  );
}
